/**
 * Clean Authentication Context
 *
 * Simple, reliable authentication using Supabase with proper state management.
 * No race conditions, no complex timeouts, just clean auth flow.
 */

import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient, User as SupabaseUser, Provider } from '@supabase/supabase-js';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';

import { AVATARS } from '../config/topicConfig';
import type { User, ParentUser, StudentUser, GuestUser, TopicType, AgeGroup } from '../types';
import { generateId } from '../utils/helpers';
import { getFriendlyDeviceInfo, getSessionLeaseId } from '../utils/sessionIdentity';
import { storage } from '../utils/storage';

// ============================================================================
// Configuration
// ============================================================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Per-step cap on how long cold-start auth may block the loading skeleton.
// Applied independently to getSession() and loadUserProfile(); overall startup
// can therefore take up to ~2× this value in the worst case.
const STARTUP_AUTH_TIMEOUT_MS = 6000;

async function raceWithTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`Auth startup operation "${label}" timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Derive a friendly display name for a newly created parent profile.
 * OAuth providers (Google/Microsoft/Facebook) expose the name under different
 * metadata keys (full_name / name), so fall back through the common ones before
 * using the email local-part.
 */
function resolveDisplayName(supabaseUser: SupabaseUser): string {
  const metadata = (supabaseUser.user_metadata ?? {}) as Record<string, unknown>;
  const candidates = [metadata.full_name, metadata.name, metadata.display_name];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  const emailLocalPart = supabaseUser.email?.split('@')[0];
  return emailLocalPart && emailLocalPart.trim() ? emailLocalPart : 'Parent';
}

// ============================================================================
// Types
// ============================================================================

type AuthState = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Social sign-in providers offered on the login screen.
 * - `google` is a built-in Supabase OAuth provider.
 * - `microsoft` is configured in Supabase as a "Custom Auth Provider" (OIDC),
 *   addressed by the SDK via the `custom:<identifier>` id below.
 */
export type OAuthProvider = 'google' | 'microsoft';

// Maps our app-level provider keys to the Supabase SDK provider ids.
const SUPABASE_PROVIDER_IDS: Record<OAuthProvider, string> = {
  google: 'google',
  microsoft: 'custom:microsoft',
};

interface AuthContextType {
  // State
  authState: AuthState;
  user: User | null;
  currentProfile: ParentUser | StudentUser | GuestUser | null;
  activeStudent: StudentUser | null;
  activeSessionId: string | null;
  supabaseEnabled: boolean;

  // Computed
  isLoading: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
  isParent: boolean;
  isAdmin: boolean;

  // Auth Actions
  signInAsGuest: (
    studentName?: string,
    ageGroup?:
      | '4-5'
      | '5-6'
      | '6-7'
      | '7-8'
      | '8-9'
      | '9-10'
      | '10-11'
      | '11-12'
      | '12-13'
      | '13-14'
      | '14-15'
      | '15-16'
      | '16-17'
      | '17-18'
  ) => void;
  signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
  signOut: () => Promise<void>;

  // Student Management
  createStudent: (studentData: CreateStudentData) => Promise<StudentUser>;
  updateStudent: (studentId: string, updates: Partial<StudentUser>) => Promise<void>;
  deleteStudent: (studentId: string) => Promise<void>;
  switchToStudent: (studentId: string) => Promise<void>;
  switchToParent: () => Promise<void>;
  verifyStudentPin: (studentId: string, pin: string) => Promise<boolean>;
  forceUnlockStudent: (studentId: string) => Promise<boolean>;
  getStudentSessionStatus: (
    studentId: string
  ) => Promise<{ hasActiveSession: boolean; sessionDevice?: string }>;

  // Profile Management
  setParentalPin: (pin: string) => Promise<void>;
  verifyParentalPin: (pin: string) => Promise<boolean>;
  updateParentProfile: (updates: Partial<ParentUser>) => Promise<void>;
  updateStudentProgress: (studentId: string, sessionData: SessionData) => Promise<void>;
}

interface CreateStudentData {
  name: string;
  avatar: string;
  ageGroup:
    | '4-5'
    | '5-6'
    | '6-7'
    | '7-8'
    | '8-9'
    | '9-10'
    | '10-11'
    | '11-12'
    | '12-13'
    | '13-14'
    | '14-15'
    | '15-16'
    | '16-17'
    | '17-18';
  maxDailyTime?: number;
  allowedTopics?: TopicType[];
  studentPin?: string;
}

interface SessionData {
  pointsEarned: number;
  questionsTotal: number;
  questionsCorrect: number;
  newStreak: number;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  // Core state
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [currentProfile, setCurrentProfile] = useState<ParentUser | StudentUser | GuestUser | null>(
    null
  );
  const [activeStudent, setactiveStudent] = useState<StudentUser | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // Refs
  const supabaseRef = useRef<SupabaseClient | null>(null);
  const initRef = useRef(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const adminCheckCacheRef = useRef<Map<string, boolean>>(new Map()); // Cache admin status per user ID

  // Computed
  const supabaseEnabled = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
  const isLoading = authState === 'loading';
  const isAuthenticated = authState === 'authenticated' && !!user && !user.isGuest;
  const isGuest = user?.isGuest ?? false;
  const isParent = user?.role === 'parent';
  const isAdmin = user?.role === 'admin';

  // ============================================================================
  // Supabase Client
  // ============================================================================

  const getSupabase = useCallback((): SupabaseClient | null => {
    if (!supabaseEnabled) return null;

    if (!supabaseRef.current) {
      supabaseRef.current = createBrowserClient(SUPABASE_URL, SUPABASE_ANON_KEY);

      // DEV/test-only seam: expose the browser client so end-to-end tests can
      // establish a session programmatically (the email/password UI was removed
      // in favour of social sign-in). Stripped from production builds.
      if (import.meta.env.DEV) {
        (window as unknown as { __mathxySupabase?: SupabaseClient }).__mathxySupabase =
          supabaseRef.current;
      }
    }
    return supabaseRef.current;
  }, [supabaseEnabled]);

  // ============================================================================
  // Initialization
  // ============================================================================

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    void initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initializeAuth(): Promise<void> {
    // Check for guest first (fastest path)
    const savedProfile = storage.get<ParentUser | StudentUser | GuestUser>('current_profile');
    if (savedProfile?.isGuest) {
      restoreGuestSession(savedProfile as GuestUser);
      return;
    }

    // No Supabase? Show welcome
    if (!supabaseEnabled) {
      setAuthState('unauthenticated');
      return;
    }

    // Check Supabase session
    const supabase = getSupabase();
    if (!supabase) {
      setAuthState('unauthenticated');
      return;
    }

    try {
      const {
        data: { session },
        error,
      } = await raceWithTimeout(
        supabase.auth.getSession(),
        STARTUP_AUTH_TIMEOUT_MS,
        'supabase.auth.getSession'
      );

      if (error || !session) {
        // Clear any stale cached data
        clearLocalState();
        setAuthState('unauthenticated');
        return;
      }

      // Valid session - load profile
      await raceWithTimeout(
        loadUserProfile(session.user),
        STARTUP_AUTH_TIMEOUT_MS,
        'loadUserProfile'
      );
    } catch (error) {
      // Includes the startup timeout case; the auth listener wired up below
      // can still pick the session back up if the network recovers later.
      console.warn('Auth initialization failed; falling back to unauthenticated:', error);
      clearLocalState();
      setAuthState('unauthenticated');
    }

    // Set up auth state listener
    setupAuthListener();
  }

  function setupAuthListener(): void {
    const supabase = getSupabase();
    if (!supabase) return;

    supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event);

      if (event === 'SIGNED_OUT' || !session) {
        clearLocalState();
        setUser(null);
        setCurrentProfile(null);
        setactiveStudent(null);
        setAuthState('unauthenticated');
        return;
      }

      // Load profile on SIGNED_IN, INITIAL_SESSION, or TOKEN_REFRESHED when a
      // valid session exists but no profile is loaded yet. This ensures recovery
      // after a startup timeout without requiring a full page reload.
      if (
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') &&
        session &&
        !currentProfile
      ) {
        await loadUserProfile(session.user);
      }
    });
  }

  // ============================================================================
  // Profile Loading
  // ============================================================================

  async function loadUserProfile(supabaseUser: SupabaseUser): Promise<void> {
    const supabase = getSupabase();
    if (!supabase) {
      setAuthState('unauthenticated');
      return;
    }

    // Stamp the user's last login (fire-and-forget; never blocks profile load).
    // The RPC updates whichever table the caller belongs to (admin or parent).
    void supabase.rpc('record_login').then(({ error }) => {
      if (error) console.debug('record_login skipped:', error.message);
    });

    try {
      // Check if admin first (with caching to avoid repeated queries)
      let adminData = null;
      const cachedIsAdmin = adminCheckCacheRef.current.get(supabaseUser.id);

      if (cachedIsAdmin === undefined) {
        // Not cached yet, check database
        try {
          const result = await supabase.from('admin_users').select('*').eq('id', supabaseUser.id);
          const rows = (result.data ?? []) as Array<Record<string, unknown>>;
          adminData = rows[0] ?? null;
          if (import.meta.env.DEV && rows.length > 1) {
            console.warn('Unexpected multiple admin_users rows for id; using first row', {
              id: supabaseUser.id,
              count: rows.length,
            });
          }
          // Cache the result (true if admin found, false otherwise)
          adminCheckCacheRef.current.set(supabaseUser.id, !!adminData);
        } catch (adminError) {
          // Silently fail if admin_users table doesn't exist or RLS blocks access
          // Cache as non-admin to avoid repeated failed queries
          adminCheckCacheRef.current.set(supabaseUser.id, false);
          console.debug('Admin check skipped:', adminError);
        }
      } else if (cachedIsAdmin) {
        // Cached as admin, fetch the data
        try {
          const result = await supabase.from('admin_users').select('*').eq('id', supabaseUser.id);
          const rows = (result.data ?? []) as Array<Record<string, unknown>>;
          adminData = rows[0] ?? null;
          if (import.meta.env.DEV && rows.length > 1) {
            console.warn('Unexpected multiple admin_users rows for id; using first row', {
              id: supabaseUser.id,
              count: rows.length,
            });
          }
        } catch {
          // Cache may be stale, ignore
        }
      }
      // If cachedIsAdmin is false, skip the query entirely

      if (adminData) {
        const adminUser: ParentUser = {
          id: adminData.id as string,
          email: adminData.email as string,
          role: 'admin',
          isGuest: false,
          displayName: adminData.display_name as string,
          avatar: AVATARS[0],
          createdAt: adminData.created_at as string,
          subscriptionTier: 'premium',
          students: [],
        };

        setUser(adminUser);
        setCurrentProfile(adminUser);
        storage.set('current_profile', adminUser);
        setAuthState('authenticated');
        return;
      }

      // Load the parent profile and their students in one request: PostgREST
      // embeds student_profiles through its parent_id foreign key.
      const { data: parentData, error: parentError } = (await supabase
        .from('parent_profiles')
        .select('*, student_profiles(*)')
        .eq('id', supabaseUser.id)
        .single()) as { data: Record<string, unknown> | null; error: unknown };

      if (parentError || !parentData) {
        // Profile doesn't exist - create it.
        // Some OAuth identities can return a user without an email address (or a
        // hidden one). The profile requires an email, so fail clearly instead of
        // inserting an invalid row.
        if (!supabaseUser.email) {
          console.error('Cannot create parent profile: OAuth user has no email address');
          // Tear down the half-established session. createBrowserClient
          // auto-refreshes tokens, so leaving the session intact would let
          // TOKEN_REFRESHED/INITIAL_SESSION events keep re-triggering this path.
          await supabase.auth.signOut();
          clearLocalState();
          setUser(null);
          setCurrentProfile(null);
          setactiveStudent(null);
          setAuthState('unauthenticated');
          return;
        }

        const result: { data: Record<string, unknown> | null; error: unknown } = await supabase
          .from('parent_profiles')
          .insert({
            id: supabaseUser.id,
            email: supabaseUser.email,
            display_name: resolveDisplayName(supabaseUser),
            subscription_tier: 'free',
          })
          .select()
          .single();

        const { data: newProfile, error: createError } = result;

        if (createError || !newProfile) {
          console.error('Failed to create profile:', createError);
          setAuthState('unauthenticated');
          return;
        }

        const parentUser = buildParentUser(newProfile, []);
        setUser(parentUser);
        setCurrentProfile(parentUser);
        storage.set('current_profile', parentUser);
        setAuthState('authenticated');
        return;
      }

      const parentUser = buildParentUser(
        parentData,
        (parentData.student_profiles ?? []) as Array<Record<string, unknown>>
      );

      // Restore active student if any
      const savedactiveStudent = storage.get<StudentUser>('active_student');
      if (savedactiveStudent) {
        const matchingStudent = parentUser.students.find((s) => s.id === savedactiveStudent.id);
        if (matchingStudent) {
          setactiveStudent(matchingStudent);
        }
      }

      setUser(parentUser);
      setCurrentProfile(parentUser);
      storage.set('current_profile', parentUser);
      setAuthState('authenticated');
    } catch (error) {
      console.error('Error loading profile:', error);
      setAuthState('unauthenticated');
    }
  }

  function buildParentUser(
    profile: Record<string, unknown>,
    students: Record<string, unknown>[]
  ): ParentUser {
    return {
      id: profile.id as string,
      email: profile.email as string,
      role: 'parent',
      isGuest: false,
      displayName: profile.display_name as string,
      avatar: (profile.avatar as string | undefined) || AVATARS[0],
      createdAt: profile.created_at as string,
      subscriptionTier:
        (profile.subscription_tier as string | undefined) === 'premium' ? 'premium' : 'free',
      students: students.map(buildStudentUser),
    };
  }

  function buildStudentUser(student: Record<string, unknown>): StudentUser {
    return {
      id: student.id as string,
      role: 'student',
      isGuest: false,
      parentId: student.parent_id as string,
      displayName: student.name as string,
      avatar: student.avatar as string,
      ageGroup: student.age_group as AgeGroup,
      createdAt: student.created_at as string,
      maxDailyTime: student.max_daily_time as number | undefined,
      allowedTopics: student.allowed_topics as TopicType[] | undefined,
      currentLevel: Math.floor(((student.total_points as number | undefined) || 0) / 100) + 1,
      totalPoints: (student.total_points as number | undefined) || 0,
      sessionsCompleted: (student.sessions_completed as number | undefined) || 0,
      totalQuestions: (student.total_questions as number | undefined) || 0,
      correctAnswers: (student.correct_answers as number | undefined) || 0,
      accuracy:
        (student.total_questions as number) > 0
          ? ((student.correct_answers as number) / (student.total_questions as number)) * 100
          : 0,
      currentStreak: (student.current_streak as number | undefined) || 0,
      bestStreak: (student.best_streak as number | undefined) || 0,
      preferences: {
        soundEnabled: true,
        musicEnabled: false,
        feedbackEnabled: false,
        difficulty: 'auto',
        theme: 'dark',
      },
    };
  }

  // ============================================================================
  // Guest Mode
  // ============================================================================

  function restoreGuestSession(guest: GuestUser): void {
    setUser(guest);
    setCurrentProfile(guest);
    setAuthState('authenticated');
  }

  function signInAsGuest(
    studentName?: string,
    ageGroup:
      | '4-5'
      | '5-6'
      | '6-7'
      | '7-8'
      | '8-9'
      | '9-10'
      | '10-11'
      | '11-12'
      | '12-13'
      | '13-14'
      | '14-15'
      | '15-16'
      | '16-17'
      | '17-18' = '5-6'
  ): void {
    const guestId = generateId();
    const guest: GuestUser = {
      id: guestId,
      role: 'guest',
      isGuest: true,
      displayName: studentName || 'Guest',
      avatar: AVATARS[Math.floor(Math.random() * AVATARS.length)],
      createdAt: new Date().toISOString(),
      questionsRemaining: 10,
      sessionId: guestId,
      ageGroup,
      currentLevel: 1,
      totalPoints: 0,
      sessionsCompleted: 0,
      correctAnswers: 0,
      totalQuestions: 0,
      accuracy: 0,
      currentStreak: 0,
      bestStreak: 0,
      preferences: {
        soundEnabled: true,
        musicEnabled: false,
        feedbackEnabled: false,
        difficulty: 'auto',
        theme: 'dark',
      },
    };

    setUser(guest);
    setCurrentProfile(guest);
    storage.set('current_profile', guest);
    setAuthState('authenticated');
  }

  // ============================================================================
  // Authentication
  // ============================================================================

  async function signInWithOAuth(provider: OAuthProvider): Promise<void> {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase.auth.signInWithOAuth({
      // Indexed access yields a widened `string`, so casting to the SDK's
      // `Provider` union is safe and lets the custom `custom:microsoft` id pass.
      provider: SUPABASE_PROVIDER_IDS[provider] as Provider,
      options: {
        redirectTo: window.location.origin,
        // Request the email claim explicitly; some providers (e.g. Microsoft)
        // omit it otherwise, which makes Supabase reject the sign-in.
        scopes: 'openid email profile',
      },
    });

    if (error) throw error;
    // The onAuthStateChange listener will handle loading the profile after redirect
  }

  async function signOut(): Promise<void> {
    // Stop heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    // Release session lock if any
    if (activeStudent && activeSessionId) {
      const supabase = getSupabase();
      if (supabase) {
        try {
          await supabase.rpc('release_session_lock', {
            p_student_id: activeStudent.id,
            p_session_id: activeSessionId,
          });
        } catch {
          // Ignore
        }
      }
    }

    // Clear local state first (ensures UI updates)
    clearLocalState();
    setUser(null);
    setCurrentProfile(null);
    setactiveStudent(null);
    setActiveSessionId(null);
    setAuthState('unauthenticated');

    // Then sign out from Supabase
    const supabase = getSupabase();
    if (supabase) {
      await supabase.auth.signOut();
    }
  }

  function clearLocalState(): void {
    storage.remove('current_profile');
    storage.remove('active_student');
    storage.remove('active_session_id');
    storage.remove('last_activity');
    // Clear admin check cache on sign out
    adminCheckCacheRef.current.clear();
  }

  // ============================================================================
  // Student Management
  // ============================================================================

  async function createStudent(studentData: CreateStudentData): Promise<StudentUser> {
    if (!currentProfile || currentProfile.role !== 'parent') {
      throw new Error('Only parents can create students');
    }

    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = (await supabase
      .from('student_profiles')
      .insert({
        parent_id: currentProfile.id,
        name: studentData.name,
        avatar: studentData.avatar,
        age_group: studentData.ageGroup,
        max_daily_time: studentData.maxDailyTime,
        allowed_topics: studentData.allowedTopics,
      })
      .select()
      .single()) as { data: Record<string, unknown> | null; error: unknown };

    if (error) throw new Error(JSON.stringify(error));

    // Set PIN if provided
    const typedData = data as Record<string, unknown>;
    if (studentData.studentPin && typedData) {
      const hashedPin = await hashPin(studentData.studentPin);
      await supabase
        .from('student_profiles')
        .update({ student_pin: hashedPin })
        .eq('id', typedData.id as string);
    }

    const newStudent = buildStudentUser(typedData);

    // Update local state
    const updatedProfile = {
      ...currentProfile,
      students: [...currentProfile.students, newStudent],
    };
    setCurrentProfile(updatedProfile);
    storage.set('current_profile', updatedProfile);

    return newStudent;
  }

  async function updateStudent(studentId: string, updates: Partial<StudentUser>): Promise<void> {
    if (!currentProfile || currentProfile.role !== 'parent') {
      throw new Error('Only parents can update students');
    }

    const supabase = getSupabase();
    if (supabase) {
      await supabase
        .from('student_profiles')
        .update({
          name: updates.displayName,
          avatar: updates.avatar,
          age_group: updates.ageGroup,
          max_daily_time: updates.maxDailyTime,
          allowed_topics: updates.allowedTopics,
        })
        .eq('id', studentId);
    }

    // Update local state
    const updatedStudents = currentProfile.students.map((student) =>
      student.id === studentId ? { ...student, ...updates } : student
    );
    const updatedProfile = { ...currentProfile, students: updatedStudents };
    setCurrentProfile(updatedProfile);
    storage.set('current_profile', updatedProfile);

    if (activeStudent?.id === studentId) {
      const updatedactiveStudent = { ...activeStudent, ...updates };
      setactiveStudent(updatedactiveStudent);
      storage.set('active_student', updatedactiveStudent);
    }
  }

  async function deleteStudent(studentId: string): Promise<void> {
    if (!currentProfile || currentProfile.role !== 'parent') {
      throw new Error('Only parents can delete students');
    }

    const supabase = getSupabase();
    if (supabase) {
      await supabase.from('student_profiles').delete().eq('id', studentId);
    }

    // Update local state
    const updatedStudents = currentProfile.students.filter((student) => student.id !== studentId);
    const updatedProfile = { ...currentProfile, students: updatedStudents };
    setCurrentProfile(updatedProfile);
    storage.set('current_profile', updatedProfile);

    if (activeStudent?.id === studentId) {
      setactiveStudent(null);
      setActiveSessionId(null);
      storage.remove('active_student');
      storage.remove('active_session_id');
    }
  }

  async function switchToStudent(studentId: string): Promise<void> {
    if (!currentProfile || currentProfile.role !== 'parent') {
      throw new Error('Only parents can switch to students');
    }

    const student = currentProfile.students.find((s) => s.id === studentId);
    if (!student) throw new Error('Student not found');

    const supabase = getSupabase();
    const leaseId = getSessionLeaseId();

    // Try to acquire session lock
    if (supabase) {
      const { data, error } = (await supabase.rpc('acquire_session_lock', {
        p_student_id: studentId,
        p_session_id: leaseId,
        p_device_info: getFriendlyDeviceInfo(),
        p_duration_minutes: 30,
      })) as { data: unknown; error: unknown };

      if (error) throw new Error(JSON.stringify(error));
      const typedData = (data || []) as Array<Record<string, unknown>>;
      if (typedData.length > 0 && !typedData[0]?.success) {
        throw new Error(
          `${student.displayName} is currently practicing on ${(typedData[0]?.locked_by_device as string) || 'another device'}`
        );
      }
    }

    setactiveStudent(student);
    setActiveSessionId(leaseId);
    storage.set('active_student', student);
    // Do not persist the lease id: it contains a tab-scoped sessionStorage id and is only
    // intended to be stable for the lifetime of this tab/runtime.

    // Start heartbeat
    startHeartbeat(studentId, leaseId);
  }

  async function switchToParent(): Promise<void> {
    // Release session lock
    if (activeStudent && activeSessionId) {
      const supabase = getSupabase();
      if (supabase) {
        try {
          await supabase.rpc('release_session_lock', {
            p_student_id: activeStudent.id,
            p_session_id: activeSessionId,
          });
        } catch {
          // Ignore
        }
      }
    }

    // Stop heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    setactiveStudent(null);
    setActiveSessionId(null);
    storage.remove('active_student');
    storage.remove('active_session_id');
  }

  function startHeartbeat(studentId: string, sessionId: string): void {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }

    heartbeatRef.current = setInterval(
      (): void => {
        void (async (): Promise<void> => {
          const supabase = getSupabase();
          if (supabase) {
            await supabase.rpc('refresh_session_lock', {
              p_student_id: studentId,
              p_session_id: sessionId,
              p_duration_minutes: 30,
            });
          }
        })();
      },
      5 * 60 * 1000
    ); // Every 5 minutes
  }

  async function verifyStudentPin(studentId: string, pin: string): Promise<boolean> {
    const supabase = getSupabase();
    if (!supabase) return true;

    const { data } = await supabase
      .from('student_profiles')
      .select('student_pin')
      .eq('id', studentId)
      .single();

    if (!data?.student_pin) return true; // No PIN set

    const hashedInput = await hashPin(pin);
    return hashedInput === data.student_pin;
  }

  async function forceUnlockStudent(studentId: string): Promise<boolean> {
    const supabase = getSupabase();
    if (!supabase) return true;

    const { error } = await supabase.rpc('force_release_session_lock', {
      p_student_id: studentId,
    });

    return !error;
  }

  async function getStudentSessionStatus(
    studentId: string
  ): Promise<{ hasActiveSession: boolean; sessionDevice?: string }> {
    const supabase = getSupabase();
    if (!supabase) return { hasActiveSession: false };

    const { data, error } = await supabase
      .from('student_profiles')
      .select('active_session_id, session_device_info, session_expires_at')
      .eq('id', studentId)
      .single();

    if (error || !data) return { hasActiveSession: false };

    const typedData = data as Record<string, unknown>;

    // Treat missing session_expires_at as "no explicit expiry" (not expired).
    const hasExpiry = typedData.session_expires_at != null;
    const isExpired =
      hasExpiry && new Date(typedData.session_expires_at as string | number | Date) <= new Date();
    const isActive = Boolean(typedData.active_session_id) && !isExpired;
    if (!isActive) return { hasActiveSession: false };

    // If this tab holds the lease, treat it as not "active elsewhere" for UI display.
    // Without this check, a parent actively practicing in one tab could see a false
    // "currently practicing" warning when opening the dashboard in another tab of
    // the same browser (because the active lease belongs to this device/tab).
    // Lease IDs are stable per-tab (device id + tab id), so this comparison remains
    // consistent even if a user rapidly switches between students.
    // Note: we intentionally do NOT suppress based on device label alone, because
    // another tab on the same device should still be considered "active elsewhere".
    const myLeaseId = getSessionLeaseId();
    const isLockedByMe = typedData.active_session_id === myLeaseId;
    if (isLockedByMe) return { hasActiveSession: false };

    return {
      hasActiveSession: true,
      sessionDevice: (typedData.session_device_info as string) || undefined,
    };
  }

  // ============================================================================
  // Profile Management
  // ============================================================================

  async function setParentalPin(pin: string): Promise<void> {
    if (!currentProfile || currentProfile.role !== 'parent') {
      throw new Error('Only parents can set PIN');
    }

    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase not configured');

    const hashedPin = await hashPin(pin);
    await supabase
      .from('parent_profiles')
      .update({ parental_pin: hashedPin })
      .eq('id', currentProfile.id);
  }

  async function verifyParentalPin(pin: string): Promise<boolean> {
    if (!currentProfile || currentProfile.role !== 'parent') return false;

    const supabase = getSupabase();
    if (!supabase) return true;

    const { data } = await supabase
      .from('parent_profiles')
      .select('parental_pin')
      .eq('id', currentProfile.id)
      .single();

    if (!data?.parental_pin) return true;

    const hashedInput = await hashPin(pin);
    return hashedInput === data.parental_pin;
  }

  async function updateParentProfile(updates: Partial<ParentUser>): Promise<void> {
    if (!currentProfile || currentProfile.role !== 'parent') {
      throw new Error('Only parents can update profile');
    }

    const supabase = getSupabase();
    if (supabase) {
      await supabase
        .from('parent_profiles')
        .update({
          display_name: updates.displayName,
          avatar: updates.avatar,
        })
        .eq('id', currentProfile.id);
    }

    const updatedProfile = { ...currentProfile, ...updates };
    setCurrentProfile(updatedProfile);
    storage.set('current_profile', updatedProfile);
  }

  async function updateStudentProgress(studentId: string, sessionData: SessionData): Promise<void> {
    const supabase = getSupabase();
    if (supabase) {
      // Use RPC or direct update
      const { data: student } = (await supabase
        .from('student_profiles')
        .select('*')
        .eq('id', studentId)
        .single()) as { data: Record<string, unknown> | null };

      if (student) {
        await supabase
          .from('student_profiles')
          .update({
            total_points: (student.total_points as number) + sessionData.pointsEarned,
            sessions_completed: (student.sessions_completed as number) + 1,
            total_questions: (student.total_questions as number) + sessionData.questionsTotal,
            correct_answers: (student.correct_answers as number) + sessionData.questionsCorrect,
            current_streak: sessionData.newStreak,
            best_streak: Math.max(student.best_streak as number, sessionData.newStreak),
          })
          .eq('id', studentId);
      }
    }

    // Update local state
    if (currentProfile?.role === 'parent') {
      const updatedStudents = currentProfile.students.map((student) => {
        if (student.id === studentId) {
          return {
            ...student,
            totalPoints: student.totalPoints + sessionData.pointsEarned,
            sessionsCompleted: student.sessionsCompleted + 1,
            totalQuestions: student.totalQuestions + sessionData.questionsTotal,
            correctAnswers: student.correctAnswers + sessionData.questionsCorrect,
            accuracy:
              ((student.correctAnswers + sessionData.questionsCorrect) /
                (student.totalQuestions + sessionData.questionsTotal)) *
              100,
            currentStreak: sessionData.newStreak,
            bestStreak: Math.max(student.bestStreak, sessionData.newStreak),
            currentLevel: Math.floor((student.totalPoints + sessionData.pointsEarned) / 100) + 1,
          };
        }
        return student;
      });

      const updatedProfile = { ...currentProfile, students: updatedStudents };
      setCurrentProfile(updatedProfile);
      storage.set('current_profile', updatedProfile);

      if (activeStudent?.id === studentId) {
        const updatedStudent = updatedStudents.find((s) => s.id === studentId);
        if (updatedStudent) {
          setactiveStudent(updatedStudent);
          storage.set('active_student', updatedStudent);
        }
      }
    }
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  async function hashPin(pin: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: AuthContextType = {
    authState,
    user,
    currentProfile,
    activeStudent,
    activeSessionId,
    supabaseEnabled,
    isLoading,
    isAuthenticated,
    isGuest,
    isParent,
    isAdmin,
    signInAsGuest,
    signInWithOAuth,
    signOut,
    createStudent,
    updateStudent,
    deleteStudent,
    switchToStudent,
    switchToParent,
    verifyStudentPin,
    forceUnlockStudent,
    getStudentSessionStatus,
    setParentalPin,
    verifyParentalPin,
    updateParentProfile,
    updateStudentProgress,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
